"use client";

import React from "react";
import { Instagram, Linkedin, Mail, MapPin, Phone, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <li key={i} data-index={i} className="h-5 w-24 bg-muted/20 animate-pulse rounded" />)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        <li data-index={0}><Link to="/HomePage" className="text-muted-foreground hover:text-primary transition-colors">Home</Link></li>
        <li data-index={1}><Link to="/ProductsPage" className="text-muted-foreground hover:text-primary transition-colors">Products</Link></li>
        <li data-index={2}><Link to="/ContactPage" className="text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center group">
            <ArrowRight className="size-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-secondary/10 border border-secondary/20 rounded-lg">
        <p className="text-secondary text-sm font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0 flex flex-col gap-3">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-0">
            <FormControl>
              <Input type="email" placeholder="Email Address" className="bg-background/50 border-muted/30 text-foreground placeholder:text-muted-foreground focus-visible:ring-secondary h-11" {...field} />
            </FormControl>
            <FormMessage className="text-destructive-foreground mt-1 text-xs" />
          </FormItem>} />

      {submitError && <p className="text-destructive-foreground text-sm">{submitError}</p>}

      <Button type="submit" variant="secondary" disabled={isSubmitting} className="w-full h-11 font-semibold tracking-wide">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Joining...
          </> : "Join"}
      </Button>
    </Form>;
}
export default function Footer() {
  const socialLinks = [{
    icon: Linkedin,
    href: "#",
    label: "LinkedIn"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }];
  return <section id="footer" className="dark bg-background text-foreground relative overflow-hidden py-16 md:py-24 px-6 border-t border-border/10">
      {}
      <div className="absolute inset-0 opacity-10 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/world_map_texture_footer.png)] bg-cover bg-center bg-no-repeat pointer-events-none mix-blend-luminosity" aria-hidden="true" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          {}
          <div className="flex flex-col items-start">
            <Link to="/HomePage" className="mb-6 inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/cosmic_exim_logo_light.png" alt="Cosmic EXIM LLC" className="h-10 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-8 max-w-xs">
              Facilitating high-quality global trade between the USA and India. We specialize in premium agricultural products, authentic spices, and eco-friendly solutions.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} data-index={i} to={social.href} aria-label={social.label} className="flex items-center justify-center size-10 rounded-full border border-white/30 text-foreground hover:bg-white/10 hover:border-white/50 transition-all duration-300">
                    <Icon className="size-4" />
                  </Link>;
            })}
            </div>
          </div>

          {}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-primary tracking-wide">Quick Links</h3>
            <MenuProvider menu_id="46">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-primary tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-muted-foreground text-sm">
                <MapPin className="size-5 text-secondary shrink-0 mt-0.5" />
                <span>Apex, North Carolina, USA</span>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground text-sm">
                <Phone className="size-5 text-secondary shrink-0" />
                <span>+1 555-123-4567</span>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground text-sm">
                <Mail className="size-5 text-secondary shrink-0" />
                <Link to="mailto:info@cosmiexim.com" className="hover:text-primary transition-colors">
                  info@cosmiexim.com
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-primary tracking-wide">Newsletter</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Subscribe to receive updates on our latest premium imports and eco-friendly product lines.
            </p>
            <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Join" successMessage="Thank you for subscribing to our newsletter!">
              <NewsletterForm />
            </FormProvider>
          </div>

        </div>

        {}
        <div className="mt-16 pt-8 border-t border-border/20 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} Cosmic EXIM LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-primary transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </section>;
}